/**
 * Entity header resource (CT-5282).
 *
 * The list endpoint returns CARDS FOR A TYPE; this returns the IDENTIFICATION
 * RECORD FOR ONE ID. Different resource, different shape, different call site —
 * hence its own service rather than an overload of the list query.
 *
 * Real signatures and snake_case bodies; only the transport is stubbed via
 * `mockTransport.js`.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockEntityHeadersForTests } from './__mocks__/entityHeader.mock.js';

/**
 * Map a raw API header onto the object `EntityHeader` consumes (CT-5272,
 * Figma DS node 19815:101548).
 *
 * Same rules as the list mapper: alias chains, no entity-type branch, and an
 * unmapped field arrives NULLISH rather than `''` so the header can omit an
 * absent block instead of rendering empty chrome. `tags` and `secondaryMeta`
 * are always arrays so the header never has to guard.
 *
 * Actions (`primaryCta`, `secondaryCta`, `menu`, `onInfoClick`) are NOT part
 * of this contract — CT-5272's own scope note is explicit that entity actions
 * are out of scope ("not raised in the call"). `EntityHeader` can render
 * those slots, but the profile shell only wires the fields below until a
 * later ticket defines real actions.
 */
const mapWorkflow = (raw) =>
  raw ? { name: raw.name ?? '', step: raw.step ?? null, total: raw.total ?? null } : null;

export const mapApiEntityHeaderToUi = (raw = {}) => ({
  id: raw.entity_id ?? raw.entityId ?? raw.id ?? '',
  title: raw.title ?? raw.name ?? raw.label ?? null,
  visual: raw.visual ?? raw.avatar_url ?? raw.avatarUrl ?? raw.image_url ?? raw.imageUrl ?? null,
  source: raw.source ?? raw.source_label ?? raw.sourceLabel ?? null,
  tags: raw.tags ?? [],
  status: raw.status ?? raw.badges?.[0] ?? null,
  description: raw.description ?? raw.summary ?? null,
  secondaryMeta: raw.secondary_meta ?? raw.secondaryMeta ?? [],
  // The workflow running on the record, started from a Next Best Action (CT-5713).
  workflow: mapWorkflow(raw.active_workflow ?? raw.activeWorkflow),
});

/**
 * Fetch the identification record for one entity.
 *
 * TODO(CT-5282 / backend): `POST /v1/workspace/profiles/entities/get` with
 * `{ entity_type, entity_id }`.
 */
export const getEntityHeader = async ({ entityType, entityId } = {}) => {
  const response = await request('/v0/profile/entities/get', {
    entity_type: entityType,
    entity_id: entityId,
  });
  return validateApiResponse(response, 'Failed to load the entity');
};
